import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Webhook, Send, CheckCircle, XCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eventOptions = ["status_change", "content_created", "content_published", "content_failed"];
const emptyForm = { name: "", url: "", events: ["status_change"], is_active: true };

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [testing, setTesting] = useState(null);
  const { t } = useLanguage();

  const load = async () => {
    try {
      const res = await axios.get(`${API}/webhooks`, { withCredentials: true });
      setWebhooks(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/webhooks/${editing}`, form, { withCredentials: true });
        toast.success("Webhook aktualisiert");
      } else {
        await axios.post(`${API}/webhooks`, form, { withCredentials: true });
        toast.success("Webhook erstellt");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch { toast.error("Fehler"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Webhook loeschen?")) return;
    try {
      await axios.delete(`${API}/webhooks/${id}`, { withCredentials: true });
      toast.success("Geloescht");
      load();
    } catch { toast.error("Fehler"); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await axios.post(`${API}/webhooks/test/${id}`, {}, { withCredentials: true });
      if (res.data.success) toast.success("Webhook Test erfolgreich!");
      else toast.error(`Webhook Test fehlgeschlagen: ${res.data.error || res.data.status}`);
    } catch { toast.error("Test fehlgeschlagen"); }
    setTesting(null);
  };

  const toggleEvent = (ev) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((e) => e !== ev) : [...f.events, ev],
    }));
  };

  return (
    <div data-testid="webhooks-page" className="space-y-6 max-w-4xl">
      <PageHelp title="Wozu brauche ich Webhooks?" storageKey="webhooks">
        <p>
          Webhooks sind <b>Benachrichtigungen, die das Studio an andere Tools sendet</b>, sobald etwas passiert
          (z. B. „Post wurde freigegeben" oder „Post wurde veroeffentlicht"). So bekommst du in Slack/Teams/Discord
          oder in deinem eigenen Tool sofort Bescheid, ohne ins Studio einloggen zu muessen.
        </p>
        <h4>Typische Anwendungsfaelle</h4>
        <ul>
          <li><b>Slack/Teams</b> — Channel benachrichtigen, wenn ein Post fertig zur Freigabe ist.</li>
          <li><b>Zapier / Make</b> — Auto-Sync: wenn Post veroeffentlicht ist, automatisch in einer Google-Tabelle protokollieren.</li>
          <li><b>Eigener Server</b> — wenn du Posts spaeter in einem CRM oder eigenen Reporting-Tool aufgreifen willst.</li>
        </ul>
        <h4>Verfuegbare Events</h4>
        <ul>
          <li><code>status_change</code> — Post-Status hat sich geaendert (draft → review → approved → scheduled → published)</li>
          <li><code>content_created</code> — Neuer Post wurde erstellt</li>
          <li><code>content_published</code> — Post wurde live geschaltet</li>
          <li><code>content_failed</code> — Veroeffentlichung ist gescheitert (z. B. Token abgelaufen)</li>
        </ul>
        <h4>So legst du einen Webhook an</h4>
        <ol>
          <li>Im Ziel-Tool <b>Incoming-Webhook-URL</b> generieren — Direktlinks:{" "}
            <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer"><b>Slack ↗</b></a> ·{" "}
            <a href="https://support.microsoft.com/office/create-incoming-webhooks-with-workflows-for-microsoft-teams-8ae491c7-0394-4861-ba59-055e33f75498" target="_blank" rel="noreferrer"><b>Teams ↗</b></a> ·{" "}
            <a href="https://zapier.com/apps/webhook/integrations" target="_blank" rel="noreferrer"><b>Zapier ↗</b></a> ·{" "}
            <a href="https://www.make.com/en/help/tools/webhooks" target="_blank" rel="noreferrer"><b>Make ↗</b></a>
          </li>
          <li>Hier <b>„+ Webhook"</b> klicken, Name vergeben, URL einfuegen, Events anhaken.</li>
          <li><b>„Test"</b> klicken → das Studio schickt eine Beispiel-Nachricht an die URL.</li>
          <li>Wenn die Nachricht im Ziel-Tool ankommt: aktivieren, fertig.</li>
        </ol>
        <p>
          <i>Nicht zu verwechseln mit OAuth-Verbindungen zu Meta/Instagram/TikTok</i> — die richtest du unter
          „Social Media" ein, nicht hier.
        </p>
      </PageHelp>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">Webhooks</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">Benachrichtigungen bei Status-Aenderungen (Slack, Teams, etc.)</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-webhook-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              <Plus className="w-4 h-4 mr-2" /> Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Webhook bearbeiten" : "Neuer Webhook"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Name</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" placeholder="z.B. Slack Benachrichtigung" />
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">URL</Label>
                <Input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} className="mt-1" placeholder="https://hooks.slack.com/..." />
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Events</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {eventOptions.map((ev) => (
                    <button key={ev} onClick={() => toggleEvent(ev)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                        form.events.includes(ev)
                          ? "border-[#2563EB] bg-[#2563EB]/10 text-[#2563EB]"
                          : "border-[var(--studio-border)] text-[var(--studio-text-muted)]"
                      }`}>{ev.replace(/_/g, " ")}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
                <Label className="text-sm">Aktiv</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); setForm(emptyForm); }}>{t("general.cancel")}</Button>
              <Button onClick={handleSave} data-testid="save-webhook-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                disabled={!form.name || !form.url}>{editing ? t("companies.update") : t("companies.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {webhooks.length === 0 ? (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardContent className="py-16 text-center">
            <Webhook className="w-12 h-12 mx-auto text-[var(--studio-text-muted)] opacity-40 mb-4" />
            <p className="text-[var(--studio-text-muted)]">Noch keine Webhooks konfiguriert.</p>
            <p className="text-sm text-[var(--studio-text-muted)] mt-1">Erhalte Benachrichtigungen in Slack, Teams oder per HTTP.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {webhooks.map((wh) => (
            <Card key={wh.id} className="border-[var(--studio-border)] shadow-none" data-testid={`webhook-${wh.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${wh.is_active ? "bg-green-100" : "bg-gray-100"}`}>
                    <Webhook className={`w-5 h-5 ${wh.is_active ? "text-green-600" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{wh.name}</p>
                      <Badge className={`text-[10px] border-0 ${wh.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                        {wh.is_active ? "Aktiv" : "Inaktiv"}
                      </Badge>
                    </div>
                    <p className="text-xs text-[var(--studio-text-muted)] truncate max-w-[300px]">{wh.url}</p>
                    <div className="flex gap-1 mt-1">
                      {wh.events?.map((ev) => (
                        <Badge key={ev} variant="outline" className="text-[9px]">{ev.replace(/_/g, " ")}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => handleTest(wh.id)}
                    disabled={testing === wh.id} className="text-xs h-7" data-testid={`test-webhook-${wh.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Test
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(wh.id); setForm({ ...emptyForm, ...wh }); setOpen(true); }}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(wh.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
